import Navigation from "@/components/Navigation";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Heart, Briefcase, Sparkles } from "lucide-react";
import { Helmet } from "react-helmet";
import SchemaMarkup from "@/components/SchemaMarkup";
import { createArticleSchema } from "@/utils/schemaHelpers";

const HoroscopoAcuario = () => {
  const articleSchema = createArticleSchema(
    "Acuario: Horóscopo Diario, Características y Compatibilidad con los 12 Signos",
    "Descubre el horóscopo diario, semanal y mensual de Acuario. Predicciones de amor, trabajo, salud y dinero para personas nacidas entre el 20 de enero y el 18 de febrero.",
    `${window.location.origin}/horoscopo-acuario`
  );

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Inicio",
        "item": window.location.origin
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Horóscopo Acuario",
        "item": `${window.location.origin}/horoscopo-acuario`
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>Acuario: Horóscopo Diario, Características y Compatibilidad con los 12 Signos</title>
        <meta name="description" content="Descubre el horóscopo diario, semanal y mensual de Acuario. Predicciones de amor, trabajo, salud y dinero para personas nacidas entre el 20 de enero y el 18 de febrero." />
        <link rel="canonical" href={`${window.location.origin}/horoscopo-acuario`} />
        <meta property="og:title" content="Acuario: Horóscopo Diario, Características y Compatibilidad" />
        <meta property="og:description" content="Descubre el horóscopo diario, semanal y mensual de Acuario. Predicciones de amor, trabajo, salud y dinero." />
        <meta property="og:url" content={`${window.location.origin}/horoscopo-acuario`} />
        <meta property="og:type" content="article" />
      </Helmet>
      <SchemaMarkup schema={articleSchema} />
      <SchemaMarkup schema={breadcrumbSchema} />
      
      <div className="min-h-screen">
        <Navigation />
        
        <div className="pt-24 pb-12 px-4">
          <div className="container mx-auto max-w-4xl">
            <div className="text-center mb-12">
              <div className="text-6xl mb-4">♒</div>
              <h1 className="text-3xl font-bold mb-4 text-glow">
                Acuario: Horóscopo Diario, Características y Compatibilidad con los 12 Signos
              </h1>
              <p className="text-xl text-muted-foreground">20 de Enero - 18 de Febrero</p>
            </div>

            <section className="mb-16">
              <h2 className="text-2xl font-bold mb-6 text-primary">Tu Horóscopo Acuario: Predicciones Actualizadas</h2>
              
              <Card className="p-8 gradient-celestial">
                <Tabs defaultValue="hoy" className="w-full">
                  <TabsList className="grid w-full grid-cols-3 mb-8">
                    <TabsTrigger value="hoy">Hoy</TabsTrigger>
                    <TabsTrigger value="semanal">Semanal</TabsTrigger>
                    <TabsTrigger value="mensual">Mensual</TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="hoy" className="space-y-6">
                    <div>
                      <h3 className="text-2xl font-bold mb-4 flex items-center gap-2">
                        <Heart className="w-6 h-6 text-primary" />
                        Horóscopo de Hoy para Acuario
                      </h3>
                      <p className="text-center text-sm text-muted-foreground mb-6">Número: 11 | Color: Azul Eléctrico</p>
                      <div className="space-y-4">
                        <div>
                          <h4 className="text-lg font-semibold mb-2 text-primary">💖 Amor</h4>
                          <p className="text-muted-foreground leading-relaxed">
                            Buscas libertad e intelectualidad en el amor. Sorprende a tu pareja con ideas originales. Valora la amistad y el espacio mutuo.
                          </p>
                        </div>
                        <div>
                          <h4 className="text-lg font-semibold mb-2 text-secondary">💼 Trabajo</h4>
                          <p className="text-muted-foreground leading-relaxed">
                            Tu originalidad es tu superpoder. Propón ideas innovadoras en un ambiente de grupo. Es un día ideal para la tecnología. Rompe el molde.
                          </p>
                        </div>
                        <div>
                          <h4 className="text-lg font-semibold mb-2 text-accent">✨ Salud</h4>
                          <p className="text-muted-foreground leading-relaxed">
                            La mente abierta te beneficiará. Experimenta con nuevas formas de ejercicio. La conexión con tu círculo social también es vital para tu bienestar emocional.
                          </p>
                        </div>
                        <div>
                          <h4 className="text-lg font-semibold mb-2 text-accent">💰 Dinero</h4>
                          <p className="text-muted-foreground leading-relaxed">
                            Las ganancias pueden llegar de fuentes inesperadas o a través de tecnología. Un proyecto grupal o una idea original pueden revolucionar tu panorama financiero.
                          </p>
                        </div>
                      </div>
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="semanal" className="space-y-6">
                    <div>
                      <h3 className="text-2xl font-bold mb-4 flex items-center gap-2">
                        <Briefcase className="w-6 h-6 text-secondary" />
                        Horóscopo Semanal: Tu Guía para los Próximos 7 Días
                      </h3>
                      <p className="text-muted-foreground leading-relaxed mb-4">
                        Una semana llena de innovación y conexiones sociales. Los primeros días te impulsan a compartir ideas revolucionarias con tu comunidad. Tu visión futurista será especialmente valorada en contextos grupales.
                      </p>
                      <p className="text-muted-foreground leading-relaxed">
                        El fin de semana trae oportunidades para explorar nuevas tecnologías o participar en causas humanitarias. Tu independencia mental atraerá personas que valoran tu originalidad. Es momento de brillar como el visionario que eres.
                      </p>
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="mensual" className="space-y-6">
                    <div>
                      <h3 className="text-2xl font-bold mb-4 flex items-center gap-2">
                        <Sparkles className="w-6 h-6 text-accent" />
                        Horóscopo Mensual Acuario: Enfoque y Oportunidades Clave
                      </h3>
                      <p className="text-muted-foreground leading-relaxed mb-4">
                        Este mes marca un período de innovación y expansión social. La primera mitad favorece proyectos grupales, avances tecnológicos y la expresión de tu individualidad única. Tu capacidad para pensar diferente será tu mayor activo.
                      </p>
                      <p className="text-muted-foreground leading-relaxed">
                        La segunda mitad invita a equilibrar tu necesidad de libertad con compromisos significativos. Es momento de revolucionar tu entorno mientras mantienes conexiones auténticas. Tu visión del futuro puede inspirar cambios importantes.
                      </p>
                    </div>
                  </TabsContent>
                </Tabs>
              </Card>
            </section>
          </div>
        </div>
      </div>
    </>
  );
};

export default HoroscopoAcuario;